"use client";

import { Button } from "@/components/ui/button";
import ClientOnly from "@/components/ui/client-only";
import { Slider } from "@/components/ui/slider";
import { Invitation } from "@/data/invitations.server";
import { useCookieState } from "@/hooks/use-cookies";
import { cva } from "class-variance-authority";
import Link from "next/link";
import { useRouter } from "next/navigation";

import useSmoothAnchors from "../hooks/useSmoothAnchors";
import { Guide } from "../promotional/guide";
import { PromoTerms } from "../promotional/promo-terms";
import Footer from "./footer";
import Header from "./header";

import { cn } from "@/components/lib/css";

/* eslint-disable @next/next/no-img-element */

function getAuthClaimUrl(
  claimKey: string,
  requestedFunding: number,
  requestedTermLength: number
) {
  // if gclid is present, set cookie
  const url = new URL(window.location.href);
  const gclid = url.searchParams.get("gclid");
  const params = {
    gclid,
    requestedFunding,
    requestedTermLength,
  };
  const queryParams = Object.entries(params)
    .filter(([, value]) => value)
    .map(([key, value]) => `${key}=${value}`)
    .join("&");
  return `/${claimKey}/get-started?${queryParams}`;
}

function OptionToggle({
  options = [91, 182, 364],
  onChange = () => {},
  value = options[0],
  color,
}: {
  options?: number[];
  value: number;
  onChange: (value: number) => void;
  color?: string;
}) {
  return (
    <div className="rounded-button bg-color-surface-card text-color-button-primary box-border flex h-12 shrink-0 flex-row items-start justify-start self-stretch overflow-hidden rounded-md p-1 text-center text-[12px] font-medium shadow-md">
      {options.map((term: number) => {
        const isSelected: boolean = term == value;
        const className: string = isSelected
          ? "cursor-pointer rounded-button bg-color-button-primary text-color-button-primary-inverted flex flex-1 flex-row items-center justify-center self-stretch border-[1px] border-solid px-3.5 py-0 shadow-[0px_2px_5px_rgba(0,_0,_0,_0.05)]"
          : "cursor-pointer flex flex-1 flex-row items-center justify-center self-stretch px-3.5 py-0";
        return (
          <div
            key={`term-${term}`}
            className={className}
            style={{
              backgroundColor: isSelected ? color : "unset",
            }}
            onClick={() => onChange(term)}
          >
            <div className="relative leading-[150%]">
              <span>{`${Math.round(term / 30.4167)} `}</span>
              <span className="text-xs">MONTHS</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function ChooseYourTerms({
  variant,
  onClick,
  href,
  color,
  programInfo,
  heroImg,
  terms = [91, 182, 364],
}: {
  variant: "xero" | "quickbooks-online" | "freshbooks" | "partner";
  onClick?: ({ funding, length }: { funding: number; length: number }) => void;
  href?: string;
  color?: string;
  programInfo?: Invitation["program_info"];
  heroImg?: string;
  terms?: number[];
}) {
  const router = useRouter();

  const {
    max_funding = programInfo?.max_loan_amount
      ? Number(programInfo.max_loan_amount)
      : 100000,
    min_funding = programInfo?.min_loan_amount
      ? Number(programInfo.min_loan_amount)
      : 10000,
    preferred_funding = min_funding + (max_funding - min_funding) / 2,
    preferred_term = terms[0],
  } = {};

  const [funding, setFunding] = useCookieState<number>(
    "requested_loan_amount",
    preferred_funding
  );
  const [length, setLength] = useCookieState<number>(
    "requested_loan_term_days",
    preferred_term
  );

  return (
    <div className="bg-color-surface-light text-color-text-branded flex w-full flex-col overflow-hidden rounded-2xl shadow-lg">
      {heroImg ? (
        <img src={heroImg} alt={"Choose your terms"} className="w-full" />
      ) : null}
      <ClientOnly>
        <div className="flex w-full flex-col gap-3 p-6">
          {!heroImg ? (
            <div className="w-full text-left text-[24px] leading-[110%] font-medium">
              Get Started
            </div>
          ) : null}
          <div className="text-left text-[14px] leading-[150%] font-normal">
            How much funding do you need?
          </div>
          <div className="border-color-tint-dim-dark bg-txt-inverted box-border flex h-[65px] flex-row items-center justify-between self-stretch rounded-xl border-[1px] border-solid px-3 py-0 text-[24px] text-black shadow-[0px_2px_5px_rgba(0,_0,_0,_0.05)]">
            <div className="flex h-10 flex-row items-center justify-start gap-[3px]">
              <div className="relative text-[24px] opacity-[0.5]">$</div>

              <div className="relative text-[28px]">
                {Number(funding).toLocaleString("en-US")}
              </div>
            </div>
          </div>

          <div className="text-txt-dimmed flex flex-col items-start justify-start gap-1.5 self-stretch text-center text-xs">
            <Slider
              max={max_funding}
              step={1000}
              min={min_funding}
              value={[funding]}
              onValueChange={([value]) => setFunding(value)}
              variant={variant}
            />
            <div className="text-txt-tertiary flex flex-row items-start justify-between self-stretch text-[12px]">
              <div
                className="cursor-pointer leading-[150%]"
                onClick={() => setFunding(min_funding)}
              >
                ${min_funding.toLocaleString("en-US")}
              </div>
              <div
                className="cursor-pointer leading-[150%]"
                onClick={() => setFunding(max_funding)}
              >
                ${max_funding.toLocaleString("en-US")}
              </div>
            </div>
          </div>
          <div className="relative box-border h-px w-full border-t-[1px] border-solid border-[#d5d5d580]" />
          <div className="text-txt-primary inline-block w-full text-left text-[14px] leading-[150%] font-normal">
            How long do you want to repay?
          </div>
          <OptionToggle
            onChange={setLength}
            value={length}
            options={terms}
            color={color}
          />
          <button
            onClick={() => {
              if (href) {
                router.push(href);
                return;
              }
              onClick?.({ funding, length });
            }}
            className={cn(
              cva(
                "rounded-button ring-offset-background focus-visible:ring-ring mt-3 inline-flex h-12 w-full items-center justify-center px-5 py-3 text-sm font-medium whitespace-nowrap text-white transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-hidden disabled:pointer-events-none disabled:opacity-50",
                {
                  variants: {
                    variant: {
                      xero: "bg-[#1AB4D7] hover:bg-[#1590AC]",
                      "quickbooks-online": "bg-[#8db849] hover:bg-[#739233]",
                      freshbooks: "bg-[#0075dd] hover:bg-[#005FAF]",
                      partner:
                        "bg-color-button-primary text-color-button-primary-inverted",
                    },
                  },
                  defaultVariants: {
                    variant: "xero",
                  },
                }
              )({ variant })
            )}
          >
            Get Started
          </button>
        </div>
      </ClientOnly>
    </div>
  );
}

function Hero({
  claimKey,
  accountingPlatform,
  icon,
  color,
}: {
  claimKey: "xero" | "quickbooks-online" | "freshbooks";
  accountingPlatform: string;
  icon: string;
  color: string;
}) {
  const router = useRouter();
  return (
    <div className="m-6 flex w-full max-w-[432px] flex-row items-center justify-center rounded-xl text-center sm:m-12 md:max-w-[936px]">
      <div className="relative flex h-full w-full flex-col gap-6 px-6 md:flex-row lg:gap-12 lg:px-12">
        <div className="flex h-full w-full max-w-[420px] flex-col items-center justify-center gap-6 lg:gap-12 lg:py-12">
          <div className="flex w-full flex-col items-start justify-start gap-3 py-6 text-left text-[36px] font-medium text-[#FCFBFC]">
            <img src={icon} alt={accountingPlatform} className="h-[68px]" />
            <h1 className="self-stretch leading-[110%]">
              Apply for a loan
              <br />
              using your {accountingPlatform} account.
            </h1>
            <div className="text-txt-inverted relative self-stretch text-[18px] leading-[150%] font-light">
              Pop is the fastest way for businesses using {accountingPlatform}{" "}
              to get funding. Skip the paperwork and long reviews–just connect
              your account to see your rates.
            </div>
          </div>
        </div>
        <div className="relative flex w-full flex-1 items-center md:max-w-[320px]">
          <ChooseYourTerms
            variant={claimKey}
            color={color}
            onClick={({ funding, length }) =>
              router.push(getAuthClaimUrl(claimKey, funding, length))
            }
          />
        </div>
      </div>
    </div>
  );
}

export default function AccountingPlatformPage({
  accountingPlatform = "Xero",
  claimKey = "xero",
  background = "/assets/background-xero.png",
  icon = "/assets/icon-xero.svg",
  color = "#0075dd",
}: {
  accountingPlatform?: string;
  claimKey?: "xero" | "quickbooks-online" | "freshbooks";
  background?: string;
  icon?: string;
  color?: string;
}) {
  useSmoothAnchors();

  return (
    <div className="flex flex-1 flex-col items-center">
      <div className="flex w-full max-w-[1440px] flex-1 flex-col">
        <Header className="border-[0px]">
          <div className="hidden flex-row items-center justify-start gap-6 p-3 text-center text-base md:flex">
            <Button className="px-6 py-3" asChild>
              <Link href="/signin">Sign In</Link>
            </Button>
          </div>
        </Header>
        <div
          className="m-3 flex min-h-[560px] flex-1 flex-col items-center justify-center overflow-hidden rounded-3xl bg-cover bg-no-repeat shadow-lg md:m-6 md:mb-0"
          style={{
            backgroundImage: `url(${background})`,
          }}
        >
          <Hero
            claimKey={claimKey}
            accountingPlatform={accountingPlatform}
            icon={icon}
            color={color}
          />
        </div>
        <div className="flex flex-1 flex-col items-center justify-center p-3 md:p-6 md:pt-12">
          <div className="w-full md:pb-12">
            <PromoTerms />
          </div>
          <Guide accountingPlatform={accountingPlatform} />
        </div>
        <Footer />
      </div>
    </div>
  );
}
